/**
 * QuotationItemsTable Component
 * Renders the sortable items table for quotation editor
 * Extracted from QuotationEditorModal for better maintainability
 */
import React from "react";
import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
} from "@dnd-kit/core";
import {
  SortableContext,
  sortableKeyboardCoordinates,
  verticalListSortingStrategy,
} from "@dnd-kit/sortable";
import {
  Table,
  TableBody,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { CheckSquare, Square, ChevronDown } from "lucide-react";
import SortableTableRow from "./SortableTableRow";

/**
 * QuotationItemsTable - Sortable table of quotation items
 * 
 * @param {Object} props
 * @param {Array} props.items - List of quotation items
 * @param {Set} props.selectedItems - Set of selected item indices
 * @param {Set} props.modifiedItems - Set of modified item indices
 * @param {Set} props.expandedItems - Set of expanded item indices
 * @param {Function} props.toggleAllSelection - Toggle all items selection
 * @param {Function} props.toggleItemSelection - Toggle single item selection
 * @param {Function} props.toggleItemExpansion - Toggle item expansion
 * @param {Function} props.handleDragEnd - Handle drag end event
 * @param {Function} props.setPreviewImage - Set preview image
 * @param {Function} props.setPreviewItemCode - Set preview item code
 * @param {Function} props.setPreviewItemId - Set preview item id
 * @param {Function} props.handleItemChange - Handle item field change
 * @param {Function} props.handleItemChangeWithLookup - Handle item change with price lookup
 * @param {Function} props.handleDeleteItem - Handle item deletion
 * @param {Function} props.handleLookupPrice - Handle price lookup
 * @param {Function} props.getItemTotalWithUtiSum - Calculate item total with UTI
 * @param {Object} props.autocompleteData - Autocomplete suggestions data
 */
const QuotationItemsTable = ({
  items,
  selectedItems,
  modifiedItems,
  expandedItems,
  toggleAllSelection,
  toggleItemSelection,
  toggleItemExpansion,
  handleDragEnd,
  setPreviewImage,
  setPreviewItemCode,
  setPreviewItemId,
  handleItemChange,
  handleItemChangeWithLookup,
  handleDeleteItem,
  handleLookupPrice,
  getItemTotalWithUtiSum,
  autocompleteData,
}) => {
  // Configure drag sensors
  const sensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: {
        distance: 8,
      },
    }),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    })
  );

  if (!items || items.length === 0) {
    return null;
  }

  return (
    <div className="border rounded-lg overflow-auto max-h-[350px]" data-testid="quotation-items-table">
      <DndContext
        sensors={sensors}
        collisionDetection={closestCenter}
        onDragEnd={handleDragEnd}
      >
        <SortableContext
          items={items.map((item, idx) => item.id || idx)}
          strategy={verticalListSortingStrategy}
        >
          <Table>
            <TableHeader className="sticky top-0 bg-white z-10">
              <TableRow>
                {/* Drag handle column */}
                <TableHead className="w-8"></TableHead>
                
                {/* Selection checkbox column */}
                <TableHead className="w-10">
                  <button
                    onClick={toggleAllSelection}
                    className="flex items-center justify-center w-6 h-6 hover:bg-slate-100 rounded"
                    data-testid="select-all-items"
                    title={selectedItems.size === items.length ? "Deseleccionar todos" : "Seleccionar todos"}
                  >
                    {selectedItems.size === items.length && items.length > 0 ? (
                      <CheckSquare className="w-4 h-4 text-blue-600" />
                    ) : (
                      <Square className="w-4 h-4 text-slate-400" />
                    )}
                  </button>
                </TableHead>
                
                {/* Item columns */}
                <TableHead className="w-24">Img</TableHead>
                <TableHead className="w-16">Código</TableHead>
                <TableHead className="min-w-[120px]">Descripción</TableHead>
                <TableHead className="w-16">Serie</TableHead>
                <TableHead className="w-16">Material</TableHead>
                <TableHead className="w-16">Color</TableHead>
                <TableHead className="w-24">Ancho</TableHead>
                <TableHead className="w-24">Alto</TableHead>
                <TableHead className="w-20">Cant.</TableHead>
                
                {/* Expand column */}
                <TableHead className="w-10 text-center" title="Expandir detalles">
                  <ChevronDown className="w-4 h-4 mx-auto text-slate-400" />
                </TableHead>
                
                {/* Actions column */}
                <TableHead className="w-10"></TableHead>
              </TableRow>
            </TableHeader>
            
            <TableBody>
              {items.map((item, index) => (
                <SortableTableRow
                  key={item.id || `item-${index}`}
                  item={item}
                  index={index}
                  isSelected={selectedItems.has(index)}
                  isModified={modifiedItems.has(index)}
                  isExpanded={expandedItems.has(index)}
                  toggleItemSelection={toggleItemSelection}
                  toggleItemExpansion={toggleItemExpansion}
                  setPreviewImage={setPreviewImage}
                  setPreviewItemCode={setPreviewItemCode}
                  setPreviewItemId={setPreviewItemId}
                  handleItemChange={handleItemChange}
                  handleItemChangeWithLookup={handleItemChangeWithLookup}
                  handleDeleteItem={handleDeleteItem}
                  handleLookupPrice={handleLookupPrice}
                  getItemTotalWithUtiSum={getItemTotalWithUtiSum}
                  autocompleteData={autocompleteData}
                />
              ))}
            </TableBody>
          </Table>
        </SortableContext>
      </DndContext>
    </div>
  );
};

export default QuotationItemsTable;
